import type { StoryElement, StoryboardProject, TemplateDefinition } from '../types';
import {
  createBlankProject,
  createBubbleElement,
  createCell,
  createCharacterElement,
  createPropElement,
  createShapeElement,
  createTextElement,
  nowIso,
  uid,
} from './factories';
import { characterAssets, propAssets } from './catalog';

const character = (id: string) => characterAssets.find((item) => item.id === id) ?? characterAssets[0];
const prop = (id: string) => propAssets.find((item) => item.id === id) ?? propAssets[0];

const withZ = (elements: StoryElement[]): StoryElement[] =>
  elements.map((element, index) => ({ ...element, zIndex: index + 1 }));

export const buildUnexpectedIdeaProject = (): StoryboardProject => {
  const project = createBlankProject('The Unexpected Idea');
  const ava1 = createCharacterElement(character('ava'), 380, 430);
  const noah1 = createCharacterElement(character('noah'), 1050, 430);
  const bubble1 = createBubbleElement('speech', 'What if we made it visual?', 350, 110);
  const chart1 = createPropElement('chart', 'Growth Chart', prop('chart').aspect, 1030, 110, 440);
  const desk1 = createPropElement('desk', 'Desk', prop('desk').aspect, 260, 520, 470);
  const desk2 = createPropElement('desk', 'Desk', prop('desk').aspect, 950, 520, 470);
  const plant = createPropElement('plant', 'Potted Plant', prop('plant').aspect, 755, 530, 170);

  const cell1 = {
    ...createCell(0, 'studio-loft'),
    title: 'The spark',
    description: 'Ava shares an unexpected idea.',
    elements: withZ([desk1, desk2, plant, ava1, noah1, chart1, bubble1]),
  };

  const ava2 = createCharacterElement(character('ava'), 380, 430, { pose: 'wave' });
  const noah2 = createCharacterElement(character('noah'), 1050, 430, { expression: 'surprised', pose: 'point' });
  const bubble2 = createBubbleElement('speech', "Let's storyboard it!", 820, 120);
  const chart2 = createPropElement('chart', 'Growth Chart', prop('chart').aspect, 1030, 100, 440);
  const desk3 = createPropElement('desk', 'Desk', prop('desk').aspect, 260, 520, 470);
  const desk4 = createPropElement('desk', 'Desk', prop('desk').aspect, 950, 520, 470);
  const plant2 = createPropElement('plant', 'Potted Plant', prop('plant').aspect, 755, 530, 170);

  const cell2 = {
    ...createCell(1, 'studio-loft'),
    title: 'The plan',
    description: 'The team turns the idea into a sequence.',
    elements: withZ([desk3, desk4, plant2, ava2, noah2, chart2, bubble2]),
  };

  const ava3 = createCharacterElement(character('ava'), 520, 470, { pose: 'celebrate' });
  const noah3 = createCharacterElement(character('noah'), 960, 470, { pose: 'celebrate' });
  const caption = createBubbleElement('caption', 'A clear story brings the idea to life.', 470, 70);
  caption.width = 660;
  caption.height = 150;
  const cell3 = {
    ...createCell(2, 'theatre-stage'),
    title: 'The reveal',
    description: 'Their finished storyboard wins the room.',
    durationMs: 2600,
    elements: withZ([ava3, noah3, caption]),
  };

  project.cells = [cell1, cell2, cell3];
  project.layout.columns = 3;
  project.layout.showTitles = true;
  project.updatedAt = nowIso();
  return project;
};

export const buildComicStripProject = (): StoryboardProject => {
  const project = createBlankProject('A Tiny Adventure');
  project.layout.columns = 3;
  const sam = character('sam');
  const scenes = ['neighbourhood-park', 'deep-forest', 'mountain-valley'];
  project.cells = scenes.map((scene, index) => {
    const hero = createCharacterElement(sam, index === 1 ? 690 : 580, 400, {
      pose: index === 0 ? 'wave' : index === 1 ? 'point' : 'celebrate',
      expression: index === 1 ? 'surprised' : 'happy',
    });
    const bubble = createBubbleElement(
      index === 2 ? 'shout' : 'speech',
      ['A map!', 'This path looks mysterious…', 'We made it!'][index],
      index === 1 ? 300 : 520,
      100,
    );
    return {
      ...createCell(index, scene),
      title: ['Discovery', 'The detour', 'The summit'][index],
      elements: withZ([hero, bubble]),
    };
  });
  return project;
};

export const buildPitchProject = (): StoryboardProject => {
  const project = createBlankProject('Product Pitch');
  project.layout.columns = 2;
  project.cells = [0, 1, 2, 3].map((index) => {
    const cell = createCell(index, index === 3 ? 'colour-wash' : 'paper-grid');
    cell.title = ['The problem', 'The insight', 'The solution', 'The impact'][index];
    const heading = createTextElement(cell.title, 120, 90, 920, 130);
    heading.textStyle = { ...heading.textStyle!, fontSize: 72, align: 'left', fill: index === 3 ? '#ffffff' : '#27243c' };
    const body = createTextElement(
      [
        'People lose time when a complex idea stays trapped in paragraphs.',
        'Visual sequences make context, decisions, and outcomes easier to understand.',
        'StoryboardSmith turns ideas into clear scenes — privately, in the browser.',
        'Faster alignment. Better presentations. Stories people remember.',
      ][index],
      130,
      260,
      900,
      300,
    );
    body.textStyle = { ...body.textStyle!, fontSize: 44, fontWeight: 550, align: 'left', fill: index === 3 ? '#ffffff' : '#3e3957' };
    const shape = createShapeElement(index === 1 ? 'star' : index === 2 ? 'arrow' : 'rectangle', 1110, 290);
    shape.width = 300;
    shape.height = 250;
    shape.appearance = { fill: index === 3 ? '#ffcb52' : '#7b6cff', stroke: index === 3 ? '#ffcb52' : '#27243c', strokeWidth: 6 };
    cell.elements = withZ([heading, body, shape]);
    return cell;
  });
  return project;
};

export const buildLessonProject = (): StoryboardProject => {
  const project = createBlankProject('Water Cycle Lesson');
  project.layout.columns = 2;
  const labels = [
    ['Evaporation', 'Sunlight warms water and turns it into vapour.'],
    ['Condensation', 'Water vapour cools and forms clouds.'],
    ['Precipitation', 'Water falls as rain or snow.'],
    ['Collection', 'Water gathers in rivers, lakes, and oceans.'],
  ];
  project.cells = labels.map(([title, body], index) => {
    const scene = createCell(index, index === 0 ? 'sunny-beach' : index === 1 ? 'mountain-valley' : index === 2 ? 'deep-forest' : 'neighbourhood-park');
    scene.title = title;
    const caption = createBubbleElement('caption', body, 300, 60);
    caption.width = 1000;
    caption.height = 150;
    const arrow = createShapeElement('arrow', index % 2 ? 280 : 980, 350);
    arrow.width = 360;
    arrow.height = 140;
    arrow.rotation = index === 1 ? 90 : index === 2 ? 180 : index === 3 ? -90 : 0;
    scene.elements = withZ([arrow, caption]);
    return scene;
  });
  return project;
};

export const buildJourneyProject = (): StoryboardProject => {
  const project = createBlankProject('Customer Journey');
  project.layout.columns = 3;
  const stages = [
    ['Discover', 'city-crossing', 'A customer notices a new option.'],
    ['Compare', 'modern-kitchen', 'They explore benefits and alternatives.'],
    ['Choose', 'studio-loft', 'A clear story builds confidence.'],
  ];
  project.cells = stages.map(([title, sceneId, body], index) => {
    const cell = createCell(index, sceneId);
    cell.title = title;
    const person = createCharacterElement(character(index === 1 ? 'riley' : 'maya'), index === 1 ? 900 : 520, 390, {
      pose: index === 0 ? 'point' : index === 1 ? 'stand' : 'celebrate',
      expression: index === 0 ? 'surprised' : 'happy',
    });
    const caption = createBubbleElement('caption', body, 350, 90);
    caption.width = 900;
    cell.elements = withZ([person, caption]);
    return cell;
  });
  return project;
};

export const buildBlankTemplate = (): StoryboardProject => createBlankProject('Untitled Storyboard');

export const templates: TemplateDefinition[] = [
  { id: 'unexpected', name: 'The Unexpected Idea', description: 'A polished three-scene sample with dialogue, characters, props, and a reveal.', accent: '#6255f6', build: buildUnexpectedIdeaProject },
  { id: 'blank', name: 'Blank Storyboard', description: 'Start with a clean cinematic cell and build your own sequence.', accent: '#34b8a0', build: buildBlankTemplate },
  { id: 'comic', name: 'Comic Strip', description: 'A playful three-panel adventure with dialogue and expressive poses.', accent: '#ff8a65', build: buildComicStripProject },
  { id: 'pitch', name: 'Product Pitch', description: 'Four structured frames for a problem, insight, solution, and impact.', accent: '#3f7ee8', build: buildPitchProject },
  { id: 'lesson', name: 'Lesson Sequence', description: 'A visual teaching sequence with captions and directional arrows.', accent: '#ffbf3f', build: buildLessonProject },
  { id: 'journey', name: 'Customer Journey', description: 'Map discovery, comparison, and decision moments.', accent: '#d35f91', build: buildJourneyProject },
];

export const createTemplateById = (id: string): StoryboardProject => {
  const template = templates.find((item) => item.id === id) ?? templates[1];
  const project = template.build();
  project.id = uid('project');
  const now = nowIso();
  project.createdAt = now;
  project.updatedAt = now;
  return project;
};
